import React from 'react';
import { Input } from 'antd';
import type { InputProps } from 'antd';

interface NumberInputProps extends Omit<InputProps, 'onChange'> {
  value?: string | number;
  onChange?: (value: string) => void;
  min?: number;
  max?: number;
  allowDecimals?: boolean;
  allowNegative?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min = 0,
  max = 1000,
  allowDecimals = false,
  allowNegative = false,
  ...props
}) => {
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (allowedKeys.includes(e.key)) {
      return;
    }

    let pattern = /[0-9]/;
    if (allowDecimals) {
      pattern = /[0-9.]/;
    }
    if (allowNegative) {
      pattern = allowDecimals ? /[0-9.-]/ : /[0-9-]/;
    }

    if (!pattern.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    let validationPattern = /^\d+$/;
    if (allowDecimals && allowNegative) {
      validationPattern = /^-?\d*\.?\d+$/;
    } else if (allowDecimals) {
      validationPattern = /^\d*\.?\d+$/;
    } else if (allowNegative) {
      validationPattern = /^-?\d+$/;
    }

    if (validationPattern.test(pastedText)) {
      const numValue = allowDecimals ? parseFloat(pastedText) : parseInt(pastedText, 10);
      if (!isNaN(numValue)) {
        let validValue = numValue;
        if (min !== undefined) validValue = Math.max(validValue, min);
        if (max !== undefined) validValue = Math.min(validValue, max);
        
        if (!allowNegative && validValue < 0) {
          validValue = 0;
        }

        const finalValue = allowDecimals ? String(validValue) : String(Math.round(validValue));
        (e.target as HTMLInputElement).value = finalValue;
        
        const changeEvent = new Event('input', { bubbles: true });
        e.target.dispatchEvent(changeEvent);
        
        if (onChange) {
          onChange(finalValue);
        }
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    if (inputValue === '') {
      onChange?.(inputValue);
      return;
    }

    const numValue = allowDecimals ? parseFloat(inputValue) : parseInt(inputValue, 10);
    if (!isNaN(numValue)) {
      let validValue = numValue;
      if (min !== undefined) validValue = Math.max(validValue, min);
      if (max !== undefined) validValue = Math.min(validValue, max);
      
      if (!allowNegative && validValue < 0) {
        validValue = 0;
      }

      const finalValue = allowDecimals ? String(validValue) : String(Math.round(validValue));
      onChange?.(finalValue);
    }
  };

  return (
    <Input
      {...props}
      type="number"
      min={min}
      max={max}
      step={allowDecimals ? 0.01 : 1}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyPress}
      onPaste={handlePaste}
    />
  );
};

export default NumberInput; 